import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { fetchUserInfo, signOutFirebase } from '../services/userService';

const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setPasswordResetRequired(false);
        setLoading(false);
        return;
      }

      try {
        const userInfo = await fetchUserInfo();
        // An expired temp password is a dead end by design (ticket 58): Firebase Auth still
        // accepts it, so the only way to refuse entry is to sign them straight back out.
        if (userInfo?.passwordResetExpired) {
          await signOutFirebase();
          setUser(null);
          setLoading(false);
          return;
        }
        setPasswordResetRequired(!!userInfo?.passwordResetRequired);
      } catch (err) {
        // Don't strand a signed-in admin on a transient lookup failure — let them through and
        // let the individual pages surface their own errors.
        console.error('ProtectedRoute: could not fetch user info:', err);
        setPasswordResetRequired(false);
      }

      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Nothing else is reachable until the temporary password has been replaced.
  if (passwordResetRequired && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (!passwordResetRequired && location.pathname === '/change-password') {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
